import { ChevronRight, ChevronDown, MessageSquare, Flag } from 'lucide-react';
import { ClassificationBadge } from './ClassificationBadge';
import { FrameOrderDetail } from './FrameOrderDetail';
import { InlineCommentInput } from './InlineCommentInput';
import { FollowUpQuickAction } from './FollowUpQuickAction';
import type { FrameOrder } from '../../types/frameOrders';

interface FrameOrderRowProps {
    order: FrameOrder;
    isExpanded: boolean;
    onToggle: () => void;
    canEdit: boolean;
    onCommentAdded: () => void;
    assigneeOptions: string[];
}

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('de-CH');
}

function formatNumber(num: number): string {
    return Math.round(num).toLocaleString('de-CH');
}

export function FrameOrderRow({ order, isExpanded, onToggle, canEdit, onCommentAdded, assigneeOptions }: FrameOrderRowProps) {
    return (
        <>
            <tr
                className={`cursor-pointer text-sm hover:bg-gray-50 ${isExpanded ? 'bg-blue-50/50' : ''}`}
                onClick={onToggle}
            >
                <td className="px-3 py-2">
                    {isExpanded
                        ? <ChevronDown className="h-4 w-4 text-gray-400" />
                        : <ChevronRight className="h-4 w-4 text-gray-400" />
                    }
                </td>
                <td className="px-3 py-2 font-medium text-gray-700">{order.CompanyCode}</td>
                <td className="px-3 py-2">
                    <div className="text-gray-900">{order.DocumentNr}</div>
                    <div className="text-xs text-gray-400">{order.DocumentType}</div>
                </td>
                <td className="px-3 py-2">
                    <ClassificationBadge code={order.ClassificationCode} label={order.ClassificationLabel} dateLastCall={order.DateLastCall} companyCode={order.CompanyCode} />
                </td>
                <td className="max-w-[200px] truncate px-3 py-2 text-gray-900">{order.CustomerName}</td>
                <td className="max-w-[160px] truncate px-3 py-2 text-gray-700">{order.ProductName}</td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(order.PostingOrOrderDate)}</td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(order.DateFirstCall)}</td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(order.DateLastCall)}</td>
                <td className="px-3 py-2 text-right font-mono text-gray-700">{formatNumber(order.Quantity)}</td>
                <td className="px-3 py-2 text-right font-mono text-gray-900">{formatNumber(order.OutstandingQuantity)}</td>
                <td className="px-3 py-2 text-right font-mono text-gray-700">{formatNumber(order.QtyOnStock)}</td>
                <td className="relative px-3 py-2">
                    <div className="flex items-center justify-end gap-1.5">
                        {order.CommentCount > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                                <MessageSquare className="h-3.5 w-3.5" />
                                {order.CommentCount}
                            </span>
                        )}
                        {canEdit && (
                            <InlineCommentInput
                                documentId={order.DocumentID}
                                onCommentAdded={onCommentAdded}
                            />
                        )}
                        {canEdit ? (
                            <FollowUpQuickAction
                                documentId={order.DocumentID}
                                hasFollowUp={!!order.HasFollowUp}
                                currentAssignee={order.FollowUpAssignee}
                                defaultAssignee={order.SalesManagerName}
                                assigneeOptions={assigneeOptions}
                                onChanged={onCommentAdded}
                            />
                        ) : order.HasFollowUp ? (
                            <span className="inline-flex items-center text-xs text-orange-500" title="Follow-up">
                                <Flag className="h-3.5 w-3.5" fill="currentColor" />
                            </span>
                        ) : null}
                    </div>
                </td>
            </tr>
            {isExpanded && (
                <tr>
                    <td colSpan={13} className="border-b border-blue-100 bg-blue-50/30 px-4 py-4">
                        <FrameOrderDetail
                            documentId={order.DocumentID}
                            canEdit={canEdit}
                            onCommentAdded={onCommentAdded}
                            assigneeOptions={assigneeOptions}
                        />
                    </td>
                </tr>
            )}
        </>
    );
}
